import React from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import EcoTag from "./EcoTag";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import useGetBuildSpinnerFrame from "../../../hooks/shared/spin/useGetBuildSpinnerFrame";
import { generateImageSet } from "../../../utils/typedUtils";
import { BnbLabel } from "../../../utils/labels";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_EXIT_PASSIVE_SUMMARY } from "../../../utils/modalConstants";
import { getEcoBadgeType, isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getGrLogo } from "../../../../../../common-deprecated/utils";
import * as Type from "../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Styles from "./styles/MiniCarHero";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { GrCode } from "../../../../../../common-deprecated/constants";
import GRLogo from "../../../../../../../assets/toyota/svg/GR/TME-GR.svg";
import GRLogoSport from "../../../../../../../assets/toyota/svg/GR/TME-GR-sport-black.svg";
import { trackChangeModel } from "../../../utils/tracking";
import { isPassiveSummary } from "../../../utils/settings/bnbSettingUtils";
import { getEcoTagType } from "../../../../../shared-logic/utils/typedUtils";
import AvailabilityBadges from "../../../../../../common-deprecated/components/aem/toyota/availability-badges/AvailabilityBadges";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import ButtonQuaternary from "../../../../../../common-deprecated/components/aem/toyota/ButtonQuaternary";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

export type MiniCarHeroPropsType = {
    showChangeLink?: boolean;
};

const MiniCarHero = (props: MiniCarHeroPropsType): JSX.Element => {
    const { showChangeLink } = props;
    const dispatch = useDispatch<BuildAndBuyDispatchType>();

    const car = useBnbSelector((state) => state.car);
    const { colour } = useBnbSelector((state) => state.basket);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const step = useBnbSelector((state) => state.navigation.step);
    const ecoTag = getEcoTagType(car.motorization.engine);
    const ecoBadge = getEcoBadgeType(commonSettings, ecoTag, car.model.id);

    const { getImage } = useGetBuildSpinnerFrame(false);
    const backgroundImage = car.model.defaultCCISBackground;
    const additionalImageOptions = backgroundImage
        ? { bgImage: backgroundImage, padding: "190,193,10,140" }
        : { bgColour: "", padding: "15" };
    const getFrame = (width: number, height: number): string => getImage(width, height, 3, additionalImageOptions);
    const image = generateImageSet(getFrame, 1170, 480);
    const grLogoCheck = getGrLogo(car.model.webtitleSectionName, car.grade.webtitleSectionName);

    const passiveSummary = isPassiveSummary(commonSettings);

    const modelStep = useStepUrl(NavStep.Model, undefined, undefined, () => trackChangeModel(step));

    return (
        <Styles.Wrapper hasBgImage={!!backgroundImage}>
            <Styles.Heading hasBgImage={!!backgroundImage}>
                <Type.H1 headingType="gamma" mb={1} color="dark">
                    <Type.ModelName ml={isRTL(commonSettings) ? 1 : 0} mr={isRTL(commonSettings) ? 0 : 1}>
                        {car.model.webtitleSectionName}
                    </Type.ModelName>
                    <Type.GradeName>{car.grade.webtitleSectionName}</Type.GradeName>
                </Type.H1>
                {car.body.webtitleSectionName && <Type.BodyTypeName>{car.body.webtitleSectionName}</Type.BodyTypeName>}

                {/* label should only be shown when there's a badge on services / summary */}
                {ecoBadge && ecoTag && <EcoTag type={ecoTag} />}

                <AvailabilityBadges
                    isSoldOut={car.soldOut}
                    isAvailableForOnlineSales={car.availableForOnlineSales}
                    isAvailableForOnlineLeasing={car.availableForOnlineLeasing}
                />

                <Styles.Actions>
                    {showChangeLink && (
                        <ButtonQuaternary hasIconRight small href={modelStep.href} onClick={modelStep.onClick}>
                            <Icon variant="trade" />
                            <BnbLabel label="changeGrade" />
                        </ButtonQuaternary>
                    )}

                    {passiveSummary && step === NavStep.Summary && (
                        <ButtonQuaternary
                            hasIconRight
                            onClick={() => {
                                dispatch(
                                    showModal({
                                        type: MODAL_EXIT_PASSIVE_SUMMARY,
                                    }),
                                );
                            }}
                        >
                            <Icon variant="trade" />
                            <BnbLabel label="passiveSummaryEditConfiguration" />
                        </ButtonQuaternary>
                    )}
                </Styles.Actions>
            </Styles.Heading>

            <Styles.HeroImageWrapper>
                <Styles.HeroImage
                    src={image.src}
                    srcSet={image.srcset}
                    sizes=""
                    draggable={false}
                    alt={`${car.model.name} ${car.grade.name} ${colour.name}`}
                />
            </Styles.HeroImageWrapper>
            {grLogoCheck && <Styles.GRLogo>{grLogoCheck === GrCode.Gr ? <GRLogo /> : <GRLogoSport />}</Styles.GRLogo>}
        </Styles.Wrapper>
    );
};

export default MiniCarHero;
